<template>
    <div class="d-flex flex-row flex-wrap">
        <v-card
            class="rounded-xl ma-2 pa-4"
            width="100%"
        >
            <v-card-title>Pricing method</v-card-title>

            <v-radio-group
                v-model="service.pricingMethod"
                row
                :disabled="$store.getters.loadingApp === 1"
                :rules="inputRulesReq"
            >
                <v-radio
                label="Standard pricing"
                value="standard"
                />

                <v-radio
                label="Price table"
                value="table"
                />
            </v-radio-group>

            <div class="text-caption">
                Standard pricing uses the service price and duration fields.
                Price table uses the prices defined in the table instead.
            </div>
        </v-card>

        <v-card
            class="rounded-xl ma-2 pa-8 d-flex flex-column"
            max-width="600px"
            v-if="service.pricingMethod === 'standard'"
        >
            <v-card-title class="mt-5">
                <v-icon
                    color="ppc_darkgreen"
                    class="mr-2"
                >
                    mdi-clock-outline
                </v-icon>
                Duration Method
            </v-card-title>

            <div class="d-flex flex-row flex-wrap mb-5">
                <v-card
                    flat
                    class="ma-2 px-5"
                    width="250px"
                >
                    <v-select
                        v-model="service.duration_type"
                        :items="sortedDurationSelect"
                        label="Duration"
                        :disabled="$store.getters.loadingApp === 1"
                    />
                </v-card>

                <v-card
                    flat
                    class="ma-2 px-5"
                    width="250px"
                >
                    <v-select
                        v-model="service.duration_text"
                        :items="sortedUnitDurationSelect"
                        label="Duration in"
                        :disabled="$store.getters.loadingApp === 1 || (service.duration_type !== 'fixed' && service.duration_type !== 'flexible')"
                    />
                </v-card>
            </div>

            <div class="d-flex flex-column mb-5">
                <v-card
                    flat
                    width="100%"
                >
                    <v-slider
                        v-model="service.min_duration"
                        class="my-4"
                        step="1"
                        ticks
                        label="Min duration"
                        tick-size="4"
                        thumb-label="always"
                        :max="24"
                        :hint="service.duration_text"
                        persistent-hint
                        thumb-color="ppc_pink"
                        track-color="ppc_grey"
                        :disabled="service.duration_type === 'fixed' || $store.getters.loadingApp === 1"
                        @change="service.duration = service.min_duration"
                    />
                </v-card>

                <v-card
                    flat
                    width="100%"
                >
                    <v-slider
                        v-model="service.max_duration"
                        class="my-4"
                        step="1"
                        ticks
                        label="Max duration"
                        tick-size="4"
                        thumb-label="always"
                        :max="24"
                        :hint="service.duration_text"
                        persistent-hint
                        thumb-color="ppc_pink"
                        track-color="ppc_grey"
                        :disabled="service.duration_type === 'fixed' || $store.getters.loadingApp === 1"
                        @change="service.duration = service.min_duration"
                    />
                </v-card>

                <v-card
                    flat
                    width="100%"
                >
                    <v-slider
                        v-model="service.duration"
                        step="1"
                        ticks
                        label="Duration"
                        tick-size="4"
                        thumb-label="always"
                        :hint="service.duration_text"
                        :disabled="service.duration_type !== 'fixed' || $store.getters.loadingApp === 1"
                        persistent-hint
                        thumb-color="ppc_pink"
                        track-color="ppc_grey"
                    />
                </v-card>
            </div>
        </v-card>

        <div class="d-flex flex-row flex-wrap">
            <!-- PRICING -->
            <v-card
                class="rounded-xl ma-2 pa-8"
                max-width="600px"
                v-if="service.pricingMethod === 'standard'"
            >
                <v-card-title class="mt-5">
                    <v-icon
                        color="ppc_darkgreen"
                        class="mr-2"
                    >
                        mdi-currency-usd
                    </v-icon>
                    Pricing
                </v-card-title>

                <div class="d-flex flex-row flex-wrap mb-5">
                    <v-card
                        flat
                        class="ma-2 px-5"
                        width="250px"
                    >
                        <v-select
                            v-model="service.price_type"
                            :items="sortedPriceTypeSelect"
                            label="Type"
                            :disabled="$store.getters.loadingApp === 1"
                        />
                    </v-card>

                    <v-card
                        flat
                        class="ma-2 px-5"
                        width="250px"
                    >
                        <v-select
                            v-model="service.price_desc"
                            :items="sortedPriceDescSelect"
                            label="Description"
                            :disabled="$store.getters.loadingApp === 1"
                        />
                    </v-card>
                </div>

                <div class="d-flex flex-row flex-wrap mb-5">
                    <v-radio-group
                        v-model="service.currency"
                        column
                        :disabled="$store.getters.loadingApp === 1"
                    >
                    <v-radio
                        label="USD"
                        class="my-0 py-0"
                        value="$"
                    />
                    <v-radio
                        label="PEN"
                        class="my-0 py-0"
                        value="S/."
                    />
                    </v-radio-group>

                    <v-text-field
                        v-model.number="service.price"
                        label="Fixed price"
                        class="ma-2 px-5"
                        style="width: 100px"
                        :disabled="$store.getters.loadingApp === 1"
                    />

                    <v-text-field
                        v-model.number="service.varPrice"
                        label="Variable price"
                        class="ma-2 px-5"
                        style="width: 100px"
                        :disabled="service.price_type === 'fixed' || $store.getters.loadingApp === 1"
                    />

                    <v-text-field
                        v-model.number="service.priceFrom"
                        label="From (overrides)"
                        class="ma-2 px-5"
                        style="width: 100px"
                        :disabled="service.price_type === 'fixed' || $store.getters.loadingApp === 1"
                    />
                </div>

                <v-card
                    flat
                    class="d-flex flex-row flex-wrap"
                    width="100%"
                >
                    <v-text-field
                    v-model.trim="service.promo_text"
                    label="Promotion text"
                    class="ma-2 px-5"
                    :rules="inputRules100max"
                    clearable
                    counter="100"
                    :disabled="$store.getters.loadingApp === 1"
                    />
                </v-card>

                <v-card
                    flat
                    class="d-flex flex-row flex-wrap"
                    width="100%"
                >
                    <v-text-field
                    v-model.trim="service.websiteText"
                    label="Website text"
                    class="ma-2 px-5"
                    :rules="inputRules100max"
                    clearable
                    counter="100"
                    :disabled="$store.getters.loadingApp === 1"
                    />
                </v-card>
                </v-card>

                <!-- PRICE TABLE -->
                <v-card class="rounded-xl ma-2 pa-8" v-if="service.pricingMethod === 'table'">
                    <v-data-table
                        :headers="headersPriceTable"
                        :items="priceTable"
                        dense
                        hide-default-footer
                        style="min-width: 300px"
                        sort-by="weeks"
                        disable-pagination
                    >
                        <template v-slot:top>
                            <div class="d-flex flex-row flex-wrap">
                                <v-card-title>
                                Price Table
                                </v-card-title>
                            </div>
                            </template>

                            <template v-slot:[`item.price`]="{ item }">
                            <v-edit-dialog
                                ref="editDialogPriceTable"
                                large
                                persistent
                                @save="$emit('save-price-table', item)"
                                @cancel="$emit('cancel-price-table', item)"
                                @open="$emit('open-price-table', item)"
                            >
                                <div>{{ item.price }}</div>

                                <template v-slot:input>
                                <v-text-field
                                    v-model="item.price"
                                    label="Edit price"
                                    single-line
                                    autofocus
                                    clearable
                                    :disabled="$store.getters.loadingApp === 1"
                                />
                                </template>
                            </v-edit-dialog>
                        </template>
                    </v-data-table>

                    <div class="d-flex flex-row">
                        <v-btn
                        class="mt-2"
                        x-small
                        @click="$emit('remove-last-week')"
                        >
                        remove last week
                        </v-btn>

                        <v-spacer />

                        <v-btn
                        class="mt-2"
                        x-small
                        @click="$emit('reset-price-table')"
                        >
                        clear table
                        </v-btn>
                    </div>
                </v-card>
        </div>
    </div>
    
</template>

<script>
    import inputRules from '@/mixins/inputRules'

    export default {
        name: 'ServiceConfiguration',

        mixins: [
            inputRules,
        ],

        computed: {

            sortedUnitDurationSelect() {
                return [...this.unitDurationSelect].sort((a, b) =>
                a.text.localeCompare(b.text)
                )
            },

            sortedDurationSelect() {
                return [...this.durationSelect].sort((a, b) =>
                a.text.localeCompare(b.text)
                )
            },

            sortedPriceTypeSelect() {
                return [...this.priceTypeSelect].sort((a, b) =>
                a.text.localeCompare(b.text)
                )
            },

            sortedPriceDescSelect() {
                return [...this.priceDescSelect].sort((a, b) =>
                a.text.localeCompare(b.text)
                )
            },

        },

        data() {
            return {
                durationUnitMap: {
                    businessWeeks: 'week(s)',
                    weeks: 'week(s)',
                    businessDays: 'day(s)',
                    nights: 'nights',
                    hours: 'hour(s)',
                },
            }
        },

        watch: {
            'service.duration_type'(newValue) {
                const unit = this.durationUnitMap[newValue]

                if (unit) {
                    this.service.duration_text = unit
                }
            },
        },

        props: {
            service: {
                type: Object,
                default: () => ({}),
            },

            priceTable: {
                type: Array,
                default: () => [],
            },

            priceTypeSelect: {
                type: Array,
                default: () => [],
            },

            priceDescSelect: {
                type: Array,
                default: () => [],
            },

            headersPriceTable: {
                type: Array,
                default: () => [],
            },

            durationSelect: {
                type: Array,
                default: () => [],
            },

            unitDurationSelect: {
                type: Array,
                default: () => [],
            },
        },
    }
</script>